import loadable from "@loadable/component";
import { inject } from "mobx-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { CurrentConditions } from "@ihr-radioedit/inferno-core";
import type { BlockFragment } from "@ihr-radioedit/inferno-webapi";
import { ForecastItem } from "../../services/Weather";
import { Container, Heading, MagicLink } from "../../ui";
import "./Weather.style.scss";
import type { Store } from "@inferno/renderer-shared-core";

const WeatherIcon = loadable(() => import("../../components/icons/WeatherIcon.component"), { ssr: false });

interface WeatherProps {
  block: BlockFragment;
  weather: CurrentConditions;
  forecast?: ForecastItem[];
  store?: Store;
}

interface SevenDayProps {
  weather_market?: string | null;
  forecast?: ForecastItem[];
}

const PoweredBy = () => {
  const { t } = useTranslation();
  return (
    <section className="weather-poweredby">
      <span>{t("Powered By")}</span>
      <img
        data-src="https://i.iheart.com/v3/re/new_assets/b967043f-c8cf-4238-bf82-a3692bf78db9"
        alt="powered by"
        className="lazyload"
      />
    </section>
  );
};

const SevenDay = ({ weather_market, forecast }: SevenDayProps) => {
  const { t } = useTranslation();
  if (!forecast) {
    return null;
  }

  return (
    <section className="seven-day-forecast">
      <Heading level={3}>{t("forecast", { weather_market })}</Heading>
      <div className="forecast-info">
        {forecast.map((item, i) => (
          <figure key={i}>
            <header>{t(item.dayOfWeek?.substr(0, 3) || "")}</header>
            <section className="weather-conditions-icon">
              <WeatherIcon iconCode={item.day?.iconCode || 44} />
            </section>
            <figcaption>
              <section className="day-phrase">{t(item.day?.wxPhraseLong || "")}</section>
              <p>
                <span className="temperature">
                  {t("Lo")} {item.temperatureMin}°
                </span>
                <span className="seperator">|</span>
                <span className="temperature">
                  {t("Hi")} {item.temperatureMax}°
                </span>
              </p>
              <p>
                {t("Precip")}: {item.day?.precipChance || "Unknown"}%
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

export const Weather = inject("store")(({ block, weather, forecast, store }: WeatherProps) => {
  if (!store) {
    return null;
  }
  const { t } = useTranslation();
  const weather_market = store.site.sections.general?.weather_market;
  useEffect(() => {
    store.storeBlock(block);
  });

  /* Note: the linter is being stupid and flagging the links to the weather network as not having unique text
    when they are using translation text and are definitely unique */
  /* tslint:disable:react-a11y-anchors */
  const icon = weather.iconCode ? (
    <div className="weather-conditions-icon">
      <WeatherIcon iconCode={weather.iconCode} />
    </div>
  ) : null;

  return (
    <Container className="component-weather">
      <section className="current-weather">
        <div className="current-weather-summary">
          <h2 className="summary-title">{t("current_weather")}</h2>
          <p className="current-location">{weather_market}</p>
          <p className="current-temp">{weather.temperature}&deg;</p>
        </div>
        {icon}
        <div className="current-weather-data">
          <p className="data-title">{t("cond")}</p>
          <ul>
            <li>
              {t("humidity")}: {weather.relativeHumidity}%
            </li>
            <li>
              {t("barometer")}: {weather.pressureAltimeter} in ({weather.pressureTendencyTrend})
            </li>
            <li>
              {t("wind")}: {weather.windSpeed} mph {weather.windDirectionCardinal}
              {weather.windSpeed}
            </li>
            <li>
              {t("visibility")}: {weather.windSpeed} {t("miles")}
            </li>
            <li>
              {t("uvIndex")}: {weather.uvIndex} ({weather.uvDescription})
            </li>
          </ul>
        </div>
        <PoweredBy />
      </section>
      <SevenDay forecast={forecast} weather_market={weather_market} />
      <ul className="weather-footer">
        <li className="weather-more">{t("More")}:</li>
        <li className="weather-link">
          <MagicLink to="https://weather.com/news?par=iheartmedia" target="_blank" label={t("weather_news")}>
            {t("weather_news")}
          </MagicLink>
        </li>
        <li className="weather-link">
          <MagicLink
            to="https://weather.com/storms/tornado-central?par=iheartmedia"
            target="_blank"
            label={t("weather_severe")}
          >
            {t("weather_severe")}
          </MagicLink>
        </li>
        <li className="copyright">{new Date().getFullYear()} The Weather Company</li>
      </ul>
    </Container>
  );
});

export default Weather;
